import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { useQueryClient } from "@tanstack/react-query";
import queryString from "query-string";

import {
  QUERY_WIDTHS,
  TABLE_MILEAGE_DI_COLUMNS,
  TABLE_MILEAGE_NO_DI_COLUMNS,
} from "@utils/index";
import { MileageModel, AssetModel } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  useAssetGetBusinessAssets,
  useMileageGetAll,
  useMileageGetOne,
  useMileageDelete,
} from "@providers/index";
import { useBusinessType, useNotify } from "@hooks/index";
import {
  PageContentContainer,
  Table,
  TableHead,
  TableBody,
  TrNoItems,
  TableRowMileage,
  BusinessSelector,
  RangePicker,
  PageContentTitle,
  Button,
  SquareRoundedPlusSvg,
  Pagination,
  ConfirmModal,
  AssetSelector,
  DesktopPageTitle,
  TutorialButton,
} from "@components/index";
import { MileageForm, MileageDetails } from "@pages/index";

export const MileageTable = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const location = useLocation();
  const idValue = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const queryClient = useQueryClient();

  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const [businessId, setBusinessId] = useState("");
  const businessType = useBusinessType(businessId);

  const [assets, setAssets] = useState<AssetModel[]>([]);
  const [assetId, setAssetId] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [mileages, setMileages] = useState<MileageModel[]>();

  const [selectedPage, setSelectedPage] = useState(1);
  const [countMileages, setCountMileages] = useState(0);
  const [perPage, setPerPage] = useState(5);

  const [redirectNeeds, setRedirectNeeds] = useState(false);

  const [detailsId, setDetailsId] = useState("");
  const [updateId, setUpdateId] = useState("");
  const [mileage, setMileage] = useState<MileageModel>();
  const [removeId, setRemoveId] = useState("");

  const isDiBusiness = useMemo(
    () =>
      businesses.find(({ uniqueId }) => uniqueId === businessId)?.type ===
      "driving_instructor",
    [businessId, businesses],
  );

  const availableTutorialNames = useMemo(() => {
    const arr = [];
    if (assets?.length) {
      arr.push("mileageCreateTutorial");
    }
    if (mileages?.length) {
      arr.push("mileageEditTutorial");
      arr.push("mileageDeleteTutorial");
    }
    return arr;
  }, [assets, mileages]);

  useEffect(() => {
    const params = queryString.parse(location.search);
    setBusinessId(
      (params?.businessId as string) ||
        defaultBusiness?.uniqueId ||
        businesses[0]?.uniqueId ||
        "",
    );
    setSelectedPage(parseInt((params?.page || "1").toString()));
    setStartDate((params?.startDate as string) || "");
    setEndDate((params?.endDate as string) || "");
    if (idValue && idValue !== "mileage") {
      setDetailsId(idValue);
    }
  }, [location, defaultBusiness, businesses, idValue]);

  const {
    isPending: isPendingGetAssets,
    isSuccess: isSuccessGetAssets,
    data: dataGetAssets,
    isError: isErrorGetAssets,
    error: errorGetAssets,
  } = useAssetGetBusinessAssets(businessId);

  useEffect(() => {
    if (isSuccessGetAssets && dataGetAssets) {
      setAssets(dataGetAssets);
      const filteredAssets = dataGetAssets.filter(
        ({ userBusiness, type }) =>
          userBusiness?.type === businessType && type === "vehicle",
      );
      const defaultAsset = filteredAssets.find(({ isDefault }) => isDefault);
      const singleAsset =
        filteredAssets.length > 1 ? undefined : filteredAssets[0];
      const params = queryString.parse(location.search);
      const assetId = params?.assetId as string;
      if (assetId === "none") {
        setAssetId("");
      } else {
        setAssetId(
          assetId || defaultAsset?.uniqueId || singleAsset?.uniqueId || "",
        );
      }
    }
  }, [isSuccessGetAssets, dataGetAssets, businessType, location.search]);

  useEffect(() => {
    if (isErrorGetAssets && errorGetAssets) {
      notify("error", errorGetAssets.message);
    }
  }, [isErrorGetAssets, errorGetAssets, notify]);

  const {
    isPending: isPendingGetAll,
    isSuccess: isSuccessGetAll,
    data: dataGetAll,
    isError: isErrorGetAll,
    error: errorGetAll,
  } = useMileageGetAll({
    businessId,
    assetId,
    startDate,
    endDate,
    page: selectedPage,
  });

  useEffect(() => {
    if (isSuccessGetAll && dataGetAll) {
      setMileages(dataGetAll.mileages);
      setCountMileages(dataGetAll.pagination.total);
      setPerPage(dataGetAll.pagination.perPage);
    }
  }, [isSuccessGetAll, dataGetAll]);

  useEffect(() => {
    if (isErrorGetAll && errorGetAll) {
      notify("error", errorGetAll.message);
    }
  }, [isErrorGetAll, errorGetAll, notify]);

  const {
    isPending: isPendingGetOne,
    isSuccess: isSuccessGetOne,
    isStale: isStaleGetOne,
    data: dataGetOne,
    isError: isErrorGetOne,
    error: errorGetOne,
  } = useMileageGetOne(
    detailsId || (updateId && updateId !== "new" ? updateId : ""),
  );

  useEffect(() => {
    if (isErrorGetOne && errorGetOne) {
      notify("error", errorGetOne.message);
    }
  }, [isErrorGetOne, errorGetOne, notify]);

  useEffect(() => {
    if (isSuccessGetOne && dataGetOne) {
      setMileage(dataGetOne);
    }
  }, [isSuccessGetOne, dataGetOne]);

  const { mutate: mutateDeleteMileage, isPending: isPendingDeleteMileage } =
    useMileageDelete(
      () => {
        notify("success", "Your Mileage has been successfully deleted.");
        handleCloseDrawer();
        setRemoveId("");
        queryClient.invalidateQueries({ queryKey: ["mileageGetAll"] });
      },
      ({ message }) => notify("error", message),
    );

  useEffect(() => {
    setState({
      isLoading:
        isPendingGetAssets ||
        isPendingGetAll ||
        (isStaleGetOne && isPendingGetOne) ||
        isPendingDeleteMileage,
    });
  }, [
    isPendingGetAssets,
    isPendingGetAll,
    isStaleGetOne,
    isPendingGetOne,
    isPendingDeleteMileage,
    setState,
  ]);

  useEffect(() => {
    if (!redirectNeeds) {
      return;
    }
    setRedirectNeeds(false);
    const params = queryString.stringify(
      {
        businessId,
        assetId,
        page: selectedPage,
        startDate,
        endDate,
      },
      { skipEmptyString: true },
    );
    navigate(`?${params}`);
  }, [
    redirectNeeds,
    navigate,
    businessId,
    assetId,
    selectedPage,
    startDate,
    endDate,
  ]);

  const handleSelectBusiness = (businessId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setBusinessId(businessId);
    setAssetId("");
  };

  const handleSelectAsset = (assetId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setAssetId(assetId || "none");
  };

  const handleSetStartDate = (startDate: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setStartDate(startDate);
  };

  const handleSetEndDate = (endDate: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setEndDate(endDate);
  };

  const handleSelectPage = (page: number) => {
    setRedirectNeeds(true);
    setSelectedPage(page);
  };

  const handleCloseDrawer = () => {
    setDetailsId("");
    setUpdateId("");
    setMileage(undefined);
    if (idValue && idValue !== "mileage") {
      navigate("/mileage");
    }
  };

  const handleDetailsMileage = (id: string) => {
    setUpdateId("");
    setDetailsId(id);
  };

  const handleEditMileage = (id: string) => {
    setDetailsId("");
    setUpdateId(id);
  };

  return (
    <div className="md:pt-6">
      {!isMobile && (
        <DesktopPageTitle
          title="Mileage Log"
          tutorialNames={availableTutorialNames}
          faqCategory={[
            "Mileage for Driving Instructors",
            "Mileage for Other Business Types",
            "Mileage for Landlord Business Type",
          ]}
        />
      )}
      <div className="flex flex-col justify-between gap-y-4 px-4 pt-4 md:flex-row md:px-10 md:pt-6">
        <BusinessSelector
          height="small"
          width="w-full md:w-64"
          value={businessId}
          onSetBusiness={handleSelectBusiness}
          dataTestid="mileage_select_businessId"
        />
        <RangePicker
          startDate={startDate}
          endDate={endDate}
          prefix="mileageTable"
          onSetStartDate={handleSetStartDate}
          onSetEndDate={handleSetEndDate}
          showPeriods={true}
          dataTestid="filter_dateRangeSelector"
        />
      </div>
      <PageContentContainer>
        <div className="flex flex-col justify-between pb-2 md:flex-row md:pb-0">
          {!isMobile && <PageContentTitle title="Mileage Log" />}
          {isMobile && (
            <div className="pb-6">
              <TutorialButton
                variant="dark"
                tutorialNames={availableTutorialNames}
                faqCategory={[
                  "Mileage for Driving Instructors",
                  "Mileage for Other Business Types",
                  "Mileage for Landlord Business Type",
                ]}
              />
            </div>
          )}
          <div className="mt-2 flex w-full flex-col gap-4 md:mt-0 md:w-auto md:flex-row">
            <div className="w-full md:w-[300px]">
              <AssetSelector
                businessType={businessType}
                items={assets}
                excludeTypes={["other", "rental_property"]}
                height="small"
                hideAddAssetItem
                hideNotAssetSpecific
                hideUploadLaterInfo
                value={assetId}
                onSetAsset={handleSelectAsset}
                dataTestid="filter_assetSelector"
              />
            </div>
            <Button
              title="Log Mileage"
              leftIcon={<SquareRoundedPlusSvg />}
              className="w-full md:w-fit md:px-5"
              height="small"
              variant="secondary"
              onClick={() => handleEditMileage("new")}
              dataTestid="button_logMileage"
            />
          </div>
        </div>
        <Table className="pb-4 md:pb-0" dataTestid="table_mileageTable">
          {!isMobile && (
            <TableHead
              columns={
                isDiBusiness
                  ? TABLE_MILEAGE_DI_COLUMNS
                  : TABLE_MILEAGE_NO_DI_COLUMNS
              }
              dataTestid="thead_mileageTable"
            />
          )}
          <TableBody dataTestid="tbody_mileageTable">
            {mileages?.length ? (
              mileages.map((mileage) => (
                <TableRowMileage
                  key={mileage.uniqueId}
                  mileage={mileage}
                  isDiBusiness={isDiBusiness}
                  onDetails={() => handleDetailsMileage(mileage.uniqueId)}
                  onEdit={() => handleEditMileage(mileage.uniqueId)}
                  onRemove={() => setRemoveId(mileage.uniqueId)}
                />
              ))
            ) : (
              <TrNoItems title="No mileage found" />
            )}
          </TableBody>
        </Table>
        {countMileages > 0 && (
          <Pagination
            count={countMileages}
            perPage={perPage}
            currentPage={selectedPage}
            onSelect={handleSelectPage}
          />
        )}
        {(updateId === "new" || (updateId && mileage)) && (
          <MileageForm
            isUpdating={updateId !== "new"}
            businessId={businessId}
            mileage={mileage}
            isDiBusiness={isDiBusiness}
            onClose={handleCloseDrawer}
          />
        )}
        {detailsId && (
          <MileageDetails
            mileage={mileage}
            onEdit={() => handleEditMileage(mileage!.uniqueId)}
            onRemove={() => mutateDeleteMileage(mileage!.uniqueId)}
            onClose={handleCloseDrawer}
          />
        )}
        <ConfirmModal
          show={!!removeId}
          confirmButtonTitle="Delete"
          text1="Are you sure?"
          text2="Do you really want to delete this entry?"
          onClickCancel={() => setRemoveId("")}
          onClickConfirm={() => mutateDeleteMileage(removeId)}
        />
      </PageContentContainer>
    </div>
  );
};
